<script setup lang="ts">
import { computed } from "vue"

const props = defineProps<{
	steps: { label: string }[]
	connections?: { from: number; to: number }[]
	highlightIndex?: number
}>()

// Compute layers using topological ordering based on connections
const layers = computed(() => {
	const stepCount = props.steps.length
	if (stepCount === 0) return []
	if (!props.connections || props.connections.length === 0) {
		// No connections: each step is its own layer
		return props.steps.map((s, i) => [{ index: i, label: s.label }])
	}

	// Calculate the depth of each step (longest path from a root)
	const inDegree = new Array(stepCount).fill(0)
	const children = new Map<number, number[]>()
	for (const c of props.connections) {
		inDegree[c.to] = (inDegree[c.to] || 0) + 1
		if (!children.has(c.from)) children.set(c.from, [])
		children.get(c.from)!.push(c.to)
	}

	// BFS to assign layers
	const depth = new Array(stepCount).fill(0)
	const queue: number[] = []
	for (let i = 0; i < stepCount; i++) {
		if (inDegree[i] === 0) queue.push(i)
	}
	while (queue.length > 0) {
		const node = queue.shift()!
		const nexts = children.get(node) ?? []
		for (const next of nexts) {
			depth[next] = Math.max(depth[next], depth[node] + 1)
			inDegree[next]--
			if (inDegree[next] === 0) queue.push(next)
		}
	}

	// Group steps by layer
	const maxDepth = Math.max(...depth)
	const result: { index: number; label: string }[][] = []
	for (let d = 0; d <= maxDepth; d++) {
		const layerSteps = props.steps
			.map((s, i) => ({ index: i, label: s.label }))
			.filter((_, i) => depth[i] === d)
		if (layerSteps.length > 0) result.push(layerSteps)
	}
	return result
})

// Check if a layer connects to the next layer (for drawing connectors)
function hasConnectionToNext(layerIdx: number): boolean {
	if (!props.connections || layerIdx >= layers.value.length - 1) return false
	const currentIndices = new Set(layers.value[layerIdx].map(s => s.index))
	const nextIndices = new Set(layers.value[layerIdx + 1].map(s => s.index))
	return props.connections.some(c => currentIndices.has(c.from) && nextIndices.has(c.to))
}
</script>

<template>
	<div class="mini-preview">
		<template v-for="(layer, layerIdx) in layers" :key="layerIdx">
			<div class="mini-preview-layer">
				<div v-for="step in layer" :key="step.index" class="mini-step-box" :class="{ 'mini-step-highlight': props.highlightIndex === step.index }">
					<span class="mini-step-label" :class="{ 'mini-step-label-highlight': props.highlightIndex === step.index }">{{ step.label }}</span>
				</div>
			</div>
			<div v-if="layerIdx < layers.length - 1 && hasConnectionToNext(layerIdx)" class="mini-connector">
				<div class="mini-connector-line"></div>
				<div class="mini-connector-arrow"></div>
			</div>
		</template>
	</div>
</template>

<style scoped>
.mini-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 8px 0;
}

.mini-preview-layer {
	display: flex;
	flex-direction: row;
	justify-content: center;
	gap: 12px;
}

.mini-step-box {
	border: 2px solid var(--p-yellow-signal-color, #f59e0b);
	border-radius: 8px;
	background-color: var(--p-yellow-signal-light-color, #fef3c7);
	padding: 6px 16px;
	min-width: 50px;
	text-align: center;
}

.mini-step-highlight {
	border-color: var(--p-green-signal-color, #22c55e);
	background-color: var(--p-green-signal-light-color, #dcfce7);
}

.mini-step-label {
	font-size: 12px;
	font-weight: 500;
	color: #92400e;
	white-space: nowrap;
}

.mini-step-label-highlight {
	color: #166534;
}

.mini-connector {
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 24px;
}

.mini-connector-line {
	width: 2px;
	height: 18px;
	background-color: var(--p-text-color-secondary, #9ca3af);
}

.mini-connector-arrow {
	width: 0;
	height: 0;
	border-left: 5px solid transparent;
	border-right: 5px solid transparent;
	border-top: 6px solid var(--p-text-color-secondary, #9ca3af);
}
</style>
